/**
 * Cadastro/edição da cliente no ERP durante a conversa. Só o nome é
 * obrigatório; o telefone vem do WhatsApp. CEP preenche o endereço.
 */
import type { ClienteErp } from "@ydchat/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { toast } from "sonner";
import { Botao } from "@/components/ui/botao";
import { Campo } from "@/components/ui/campo";
import { Rotulo } from "@/components/ui/seletor";
import { api, ErroApi } from "@/lib/api";

type Resultado =
  | { status: "concluida"; existente: boolean; cliente: ClienteErp }
  | { status: "pendente"; mensagem: string };

async function buscarCep(cep: string) {
  const d = cep.replace(/\D/g, "");
  if (d.length !== 8) return null;
  try {
    const r = await fetch(`https://viacep.com.br/ws/${d}/json/`);
    const j = (await r.json()) as {
      erro?: unknown;
      logradouro?: string;
      bairro?: string;
      localidade?: string;
      uf?: string;
    };
    return j.erro ? null : j;
  } catch {
    return null;
  }
}

export function FormCliente({
  contatoId,
  modo,
  inicial,
  nomeSugerido,
  podeVerDocumento,
  onConcluido,
}: {
  contatoId: string;
  modo: "cadastrar" | "editar";
  inicial: ClienteErp | null;
  nomeSugerido: string;
  podeVerDocumento: boolean;
  onConcluido: () => void;
}) {
  const qc = useQueryClient();
  const e = inicial?.endereco;
  const [nome, setNome] = useState(
    inicial?.nome ?? (/\d{6,}/.test(nomeSugerido) ? "" : nomeSugerido),
  );
  const [email, setEmail] = useState(inicial?.email ?? "");
  // Vendedora não vê o CPF já cadastrado: campo vazio = "não mexer".
  const cpfOculto = !podeVerDocumento && !!inicial?.cpfCnpj;
  const [cpf, setCpf] = useState(cpfOculto ? "" : (inicial?.cpfCnpj ?? ""));
  const [nascimento, setNascimento] = useState(inicial?.dataNascimento ?? "");
  const [cep, setCep] = useState(e?.cep ?? "");
  const [logradouro, setLogradouro] = useState(e?.logradouro ?? "");
  const [numero, setNumero] = useState(e?.numero ?? "");
  const [complemento, setComplemento] = useState(e?.complemento ?? "");
  const [bairro, setBairro] = useState(e?.bairro ?? "");
  const [cidade, setCidade] = useState(e?.cidade ?? "");
  const [uf, setUf] = useState(e?.uf ?? "");
  const [buscandoCep, setBuscandoCep] = useState(false);

  const salvar = useMutation({
    mutationFn: () => {
      const corpo = {
        nome,
        email: email || null,
        ...(cpfOculto && !cpf ? {} : { cpfCnpj: cpf || null }),
        dataNascimento: nascimento || null,
        endereco: { cep, logradouro, numero, complemento, bairro, cidade, uf },
      };
      return modo === "editar"
        ? api.patch<Resultado>(`/contatos/${contatoId}/cliente-erp`, corpo)
        : api.post<Resultado>(`/contatos/${contatoId}/cliente-erp`, corpo);
    },
    onSuccess: (r) => {
      if (r.status === "pendente") toast.message(r.mensagem);
      else if (r.existente)
        toast.success(`Já existia no ERP: ligada ao cadastro de ${r.cliente.nome}.`);
      else
        toast.success(
          modo === "editar" ? "Cadastro atualizado no ERP!" : "Cliente cadastrada no ERP!",
        );
      void qc.invalidateQueries({ queryKey: ["contexto", contatoId] });
      void qc.invalidateQueries({ queryKey: ["conversas"] });
      onConcluido();
    },
    onError: (err) =>
      toast.error(err instanceof ErroApi ? err.message : "Não foi possível salvar."),
  });

  const aoMudarCep = async (v: string) => {
    setCep(v);
    if (v.replace(/\D/g, "").length !== 8) return;
    setBuscandoCep(true);
    const r = await buscarCep(v);
    setBuscandoCep(false);
    if (!r) return;
    setLogradouro(r.logradouro ?? "");
    setBairro(r.bairro ?? "");
    setCidade(r.localidade ?? "");
    setUf(r.uf ?? "");
  };

  const enviar = (ev: FormEvent) => {
    ev.preventDefault();
    salvar.mutate();
  };

  return (
    <form onSubmit={enviar} className="space-y-4">
      <Rotulo texto="Nome completo">
        <Campo
          value={nome}
          onChange={(ev) => setNome(ev.target.value)}
          required
          minLength={2}
          maxLength={120}
          autoFocus
        />
      </Rotulo>
      <div className="grid grid-cols-2 gap-3">
        <Rotulo
          texto="CPF/CNPJ"
          ajuda={cpfOculto ? "Já cadastrado (só a gerência vê)." : "Evita cadastro duplicado."}
        >
          <Campo
            value={cpf}
            onChange={(ev) => setCpf(ev.target.value)}
            inputMode="numeric"
            maxLength={18}
            placeholder={cpfOculto ? "•••" : ""}
          />
        </Rotulo>
        <Rotulo texto="Nascimento">
          <Campo type="date" value={nascimento} onChange={(ev) => setNascimento(ev.target.value)} />
        </Rotulo>
      </div>
      <Rotulo texto="E-mail">
        <Campo
          type="email"
          value={email}
          onChange={(ev) => setEmail(ev.target.value)}
          maxLength={255}
        />
      </Rotulo>
      <fieldset className="space-y-3">
        <legend className="mb-1.5 text-sm font-semibold text-neutral-800">Endereço</legend>
        <div className="grid grid-cols-[1fr_auto] items-end gap-2">
          <Rotulo texto="CEP">
            <Campo
              value={cep}
              onChange={(ev) => void aoMudarCep(ev.target.value)}
              inputMode="numeric"
              maxLength={9}
            />
          </Rotulo>
          {buscandoCep && <Loader2 size={18} className="mb-3 animate-spin text-neutral-400" />}
        </div>
        <div className="grid grid-cols-[1fr_96px] gap-2">
          <Rotulo texto="Rua">
            <Campo
              value={logradouro}
              onChange={(ev) => setLogradouro(ev.target.value)}
              maxLength={120}
            />
          </Rotulo>
          <Rotulo texto="Número">
            <Campo value={numero} onChange={(ev) => setNumero(ev.target.value)} maxLength={20} />
          </Rotulo>
        </div>
        <Rotulo texto="Complemento">
          <Campo
            value={complemento}
            onChange={(ev) => setComplemento(ev.target.value)}
            maxLength={120}
          />
        </Rotulo>
        <div className="grid grid-cols-[1fr_1fr_64px] gap-2">
          <Rotulo texto="Bairro">
            <Campo value={bairro} onChange={(ev) => setBairro(ev.target.value)} maxLength={120} />
          </Rotulo>
          <Rotulo texto="Cidade">
            <Campo value={cidade} onChange={(ev) => setCidade(ev.target.value)} maxLength={120} />
          </Rotulo>
          <Rotulo texto="UF">
            <Campo
              value={uf}
              onChange={(ev) => setUf(ev.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
            />
          </Rotulo>
        </div>
      </fieldset>
      <p className="text-xs text-neutral-500">
        O telefone do WhatsApp entra no cadastro automaticamente. Se o ERP já tiver esta cliente
        (mesmo telefone ou CPF), o cadastro existente é usado.
      </p>
      <Botao type="submit" className="w-full justify-center" disabled={salvar.isPending}>
        {salvar.isPending && <Loader2 size={16} className="animate-spin" />}
        {modo === "editar" ? "Salvar no ERP" : "Cadastrar no ERP"}
      </Botao>
    </form>
  );
}
